import { useState, useEffect, useCallback } from 'react';
import { logger } from '@/lib/logger';

export type Theme = 'light' | 'dark' | 'system';
export type DefaultReplyAction = 'stay_on_ticket' | 'show_open_tickets' | 'show_next_ticket';
export type EditorMode = 'markdown' | 'wysiwyg';

interface UserPreferences {
  soundEnabled: boolean;
  theme: Theme;
  autoFocusReply: boolean;
  defaultReplyAction: DefaultReplyAction;
  editorMode: EditorMode;
}

const DEFAULT_PREFERENCES: UserPreferences = {
  soundEnabled: false,
  theme: 'system',
  autoFocusReply: true,
  defaultReplyAction: 'stay_on_ticket',
  editorMode: 'wysiwyg',
};

const STORAGE_KEY = 'luminjo_user_preferences';

export function useUserPreferences() {
  const [preferences, setPreferences] = useState<UserPreferences>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return { ...DEFAULT_PREFERENCES, ...JSON.parse(stored) };
      }
    } catch (e) {
      logger.error('Failed to read preferences from localStorage', e, 'useUserPreferences');
    }
    return DEFAULT_PREFERENCES;
  });

  // Apply theme on mount and when it changes
  useEffect(() => {
    applyTheme(preferences.theme);
  }, [preferences.theme]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    } catch (e) {
      logger.error('Failed to save preferences to localStorage', e, 'useUserPreferences');
    }
  }, [preferences]);

  const updatePreference = useCallback(<K extends keyof UserPreferences>(
    key: K,
    value: UserPreferences[K]
  ) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
  }, []);

  const toggleSound = useCallback(() => {
    setPreferences(prev => ({ ...prev, soundEnabled: !prev.soundEnabled }));
  }, []);

  const setTheme = useCallback((theme: Theme) => {
    setPreferences(prev => ({ ...prev, theme }));
  }, []);

  const toggleAutoFocusReply = useCallback(() => {
    setPreferences(prev => ({ ...prev, autoFocusReply: !prev.autoFocusReply }));
  }, []);

  const setDefaultReplyAction = useCallback((action: DefaultReplyAction) => {
    setPreferences(prev => ({ ...prev, defaultReplyAction: action }));
  }, []);

  const setEditorMode = useCallback((mode: EditorMode) => {
    setPreferences(prev => ({ ...prev, editorMode: mode }));
  }, []);

  return {
    preferences,
    updatePreference,
    toggleSound,
    setTheme,
    toggleAutoFocusReply,
    setDefaultReplyAction,
    setEditorMode,
  };
}

// Apply theme to document
function applyTheme(theme: Theme) {
  const root = document.documentElement;
  
  if (theme === 'system') {
    const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    root.classList.remove('light', 'dark');
    root.classList.add(systemTheme);
  } else {
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
  }
}

// Guard against duplicate registration (HMR, accidental re-imports)
let themeInitialized = false;

// Initialize theme on app load
export function initializeTheme() {
  if (themeInitialized) return;
  themeInitialized = true;

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const prefs = JSON.parse(stored);
      applyTheme(prefs.theme || 'system');
    }
  } catch (e) {
    applyTheme('system');
  }

  // Listen for system theme changes (no cleanup: listener lives for the page lifetime)
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const prefs = JSON.parse(stored);
        if (prefs.theme === 'system') {
          applyTheme('system');
        }
      }
    } catch (err) {
      // ignore
    }
  });
}

// Notification sound using Web Audio API
let audioContext: AudioContext | null = null;

export function playNotificationSound() {
  try {
    // Check if sound is enabled
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const prefs = JSON.parse(stored);
      if (prefs.soundEnabled === false) return;
    }

    // Create or reuse audio context
    if (!audioContext) {
      audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    }

    // Resume if suspended (browser autoplay policy)
    if (audioContext.state === 'suspended') {
      audioContext.resume();
    }

    const now = audioContext.currentTime;

    // Create a gentle "pop" sound
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    // Soft, pleasant tone
    oscillator.frequency.setValueAtTime(880, now); // A5 note
    oscillator.frequency.exponentialRampToValueAtTime(440, now + 0.1); // Slide down to A4
    oscillator.type = 'sine';

    // Quick fade in/out for a soft pop
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(0.15, now + 0.02);
    gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.15);

    oscillator.start(now);
    oscillator.stop(now + 0.15);
  } catch (e) {
    logger.error('Failed to play notification sound', e, 'useUserPreferences');
  }
}
